/* W72 STEP 5 (4) — THE LIBRARY'S WORKOUTS, AS PURE FUNCTIONS (Proposal 5, with his no-chips-in-rows word).
 *
 * A row states: the name; ONE caption — the exercise count, the parts by name, the formats and the supersets (W88,
 * `listCaption`) — and, in the accent colour, as TEXT, never a pill, "N rows without a rule" (W109 took `Next on` out
 * of the row, 2026-09-30). The chips — All · Assigned this week · Never assigned ·
 * Tasks — sit in the filter row and nowhere in a row. Sorted by last used.
 *
 * ⚠️ NO `~time` COLUMN, AND THAT IS A MEASUREMENT, NOT AN OMISSION (Proposal 5 lists one). MEASURED 2026-09-25: no
 * duration estimate exists for a coach's workout on EITHER side — `AssignedWorkoutBridge` passes
 * `estimatedMinutes: nil` for every coached workout, so the phone prints none. A web estimate would be a SECOND rule
 * the phone does not have; filed in the step-5 REPORT for Fable rather than invented here.
 */
import { FORMAT_LABEL, lastUsedByTemplate, sectionName, type SectionFormat } from "./builder";
import { mondayKey } from "./weekLine";
import { shiftDayKey } from "./days";
import { missingRuleCount } from "./progressionRule";

export type RawShelfWorkout = {
  id: string; name: string; description: string | null; kind: string;
  coach_workout_sections: {
    id: string; name: string | null; position: number; format: string;
    coach_workout_exercises: { id: string; exercise_id: string | null; position: number; superset_with_next: boolean }[] | null;
  }[] | null;
};

export type RawShelfAssignment = {
  workout_id: string | null; root_workout_id: string | null; client_id: string | null; scheduled_date: string | null;
};

export type WorkoutShelfRow = {
  id: string; name: string; description: string | null; isTask: boolean;
  /** The coach's section names, in order — the NAMED ones only (W104: an unnamed section has no generated name). */
  sectionNames: string[];
  /** The formats present other than straight sets, in section order, each once. */
  formats: string[];
  /** Pairs joined by `superset_with_next` — a flag on a section's LAST row joins nothing (`CoachPairing`'s rule). */
  paired: number;
  /** 🔴 W88 — the supersets themselves: each linked RUN once (a run of three is one superset, two joins). */
  supersets: number;
  exercises: number;
  sections: number;
  /** Every library exercise the template prescribes, one per row. */
  exerciseIds: string[];
  lastUsedISO: string | null;
  /** Every assigned day of this template: who and when. */
  assigned: { clientId: string; date: string }[];
};

export function workoutShelfRows(
  workouts: readonly RawShelfWorkout[], assignments: readonly RawShelfAssignment[],
): WorkoutShelfRow[] {
  const lastUsed = lastUsedByTemplate(assignments.map((a) => ({
    workout_id: a.workout_id, root_workout_id: a.root_workout_id, scheduled_date: a.scheduled_date,
  })) as Parameters<typeof lastUsedByTemplate>[0]);
  return workouts.map((w) => {
    // A nested embed arrives in no guaranteed order — sorted here, where a test can see it.
    const sections = [...(w.coach_workout_sections ?? [])].sort((a, b) => a.position - b.position);
    const formats: string[] = [];
    let paired = 0;
    let supersets = 0;
    const exerciseIds: string[] = [];
    let exercises = 0;
    for (const s of sections) {
      if (s.format !== "straight_sets") {
        const label = FORMAT_LABEL[s.format as SectionFormat] ?? s.format;
        if (!formats.includes(label)) formats.push(label);
      }
      const rows = [...(s.coach_workout_exercises ?? [])].sort((a, b) => a.position - b.position);
      exercises += rows.length;
      rows.forEach((r, i) => {
        if (r.exercise_id !== null) exerciseIds.push(r.exercise_id);
        if (r.superset_with_next && i < rows.length - 1) {
          paired += 1;
          if (i === 0 || !rows[i - 1]!.superset_with_next) supersets += 1;
        }
      });
    }
    const assigned = assignments
      .filter((a) => (a.root_workout_id ?? a.workout_id) === w.id && a.client_id !== null && a.scheduled_date !== null)
      .map((a) => ({ clientId: a.client_id!, date: a.scheduled_date! }));
    return {
      id: w.id, name: w.name, description: w.description, isTask: w.kind === "task",
      // W104 — the NAMED sections only: an unnamed one has no generated name (its exercises still count).
      sectionNames: sections.flatMap((s) => { const n = sectionName(s.name); return n === null ? [] : [n]; }),
      formats, paired, supersets, exercises, sections: sections.length, exerciseIds,
      lastUsedISO: lastUsed.get(w.id) ?? null, assigned,
    };
  });
}

/** The first caption: the sections by name, the first four then `+ N more`. */
export function sectionCaption(row: Pick<WorkoutShelfRow, "sectionNames">): string {
  const n = row.sectionNames;
  if (n.length === 0) return "";
  if (n.length <= 4) return n.join(" · ");
  return `${n.slice(0, 4).join(" · ")} + ${String(n.length - 4)} more`;
}


/**
 * 🔴 W88 — THE ROW'S ONE CAPTION (his yes to the structure proposal, 2026-09-28): `6 exercises · Warm-up, Main work, Finisher ·
 * 1 superset`. The bare EXERCISES and SECTIONS number columns fold into it — a figure with a word beside it, under the
 * name, rather than two columns of digits. The formats other than straight sets stay (a superset is counted, never named
 * twice); a task says it is one.
 */
export function listCaption(row: Pick<WorkoutShelfRow, "isTask" | "exercises" | "sectionNames" | "formats" | "supersets">): string {
  const n = row.exercises;
  const count = n === 0 ? (row.isTask ? "" : "No exercises yet") : `${String(n)} ${n === 1 ? "exercise" : "exercises"}`;
  const parts: string[] = [];
  if (row.isTask) parts.push(count === "" ? "Task" : `Task · ${count}`);
  else parts.push(count);
  const names = sectionCaption(row);
  if (names !== "") parts.push(names.split(" · ").join(", "));
  for (const f of row.formats) if (!(f === FORMAT_LABEL.superset && row.supersets > 0)) parts.push(f);
  if (row.supersets > 0) parts.push(`${String(row.supersets)} ${row.supersets === 1 ? "superset" : "supersets"}`);
  return parts.join(" · ");
}

/* W109 (Ioannis, 2026-09-30: "the next on is too much of info that we don't need") — `nextOnText`, W88's `Next on`
   sentence, LEFT with its column: the Workouts row was its only reader (measured). The chips (`inChip`) still read the
   same `assigned` dates. */

/** "N rows without a rule" — the rows a client is prescribed that no rule will ever progress. Null when every row has
 *  one, and null when the library has not answered (an unknown id is never counted as missing). */
export function ruleGapCaption(exerciseIds: readonly string[], ruleById: ReadonlyMap<string, boolean>): string | null {
  // The builder header's own counter (`progressionRule.missingRuleCount`), so the list and the builder cannot differ.
  const { missing } = missingRuleCount(exerciseIds.map((exerciseId) => ({ exerciseId })), ruleById);
  if (missing === 0) return null;
  return missing === 1 ? "1 row without a rule" : `${String(missing)} rows without a rule`;
}


export type WorkoutChip = "all" | "thisWeek" | "never" | "tasks";

export const WORKOUT_CHIPS: readonly { chip: WorkoutChip; label: string }[] = [
  { chip: "all", label: "All" },
  { chip: "thisWeek", label: "Assigned this week" },
  { chip: "never", label: "Never assigned" },
  { chip: "tasks", label: "Tasks" },
];

/** Does a row belong under a chip? The week is Monday-first (`mondayKey`, never the locale's). */
export function inChip(row: WorkoutShelfRow, chip: WorkoutChip, today: Date): boolean {
  switch (chip) {
    case "all": return true;
    case "never": return row.assigned.length === 0;
    case "tasks": return row.isTask;
    case "thisWeek": {
      const monday = mondayKey(today);
      const sunday = shiftDayKey(new Date(`${monday}T00:00:00Z`), 6);
      return row.assigned.some((a) => a.date >= monday && a.date <= sunday);
    }
  }
}

/** Every chip's count, for the numbers beside the words. */
export function chipCounts(rows: readonly WorkoutShelfRow[], today: Date): Record<WorkoutChip, number> {
  const out = { all: 0, thisWeek: 0, never: 0, tasks: 0 } as Record<WorkoutChip, number>;
  for (const c of WORKOUT_CHIPS) out[c.chip] = rows.filter((r) => inChip(r, c.chip, today)).length;
  return out;
}
